import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, IconButton, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Menu,
    ListItemIcon, ListItemText, FormControlLabel, Switch, Alert, Tooltip,
    useMediaQuery, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BadgeIcon from '@mui/icons-material/Badge';

import api from '../../services/api';
import { isVisualizador } from '../../services/auth';
import { toastSuccess, alertValidacao, confirmAcao } from '../../services/alerts';
import { formatarData } from '../../services/convenios';
import { registrarRotuloDeRota } from '../../services/navegacao';

const estadoInicial = () => ({
    idPlanoSaude: '', numeroCarteirinha: '', validadeCarteirinha: '',
    titular: true, nomeTitular: '', ativo: true, padrao: false,
});

export default function CarteirinhasPaciente() {
    const navigate = useNavigate();
    const { id } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const soLeitura = isVisualizador();

    const [paciente, setPaciente] = useState(null);
    const [carteirinhas, setCarteirinhas] = useState([]);
    const [convenios, setConvenios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [dialogAberto, setDialogAberto] = useState(false);
    const [editandoId, setEditandoId] = useState(null);
    const [form, setForm] = useState(estadoInicial());

    const [menuAnchor, setMenuAnchor] = useState({ el: null, item: null });
    const abrirMenu = (e, item) => setMenuAnchor({ el: e.currentTarget, item });
    const fecharMenu = () => setMenuAnchor(prev => ({ ...prev, el: null }));

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [pac, vinculos] = await Promise.all([
                api.get(`/pacientes/${id}`),
                api.get(`/pacientes/${id}/convenios`),
            ]);
            setPaciente(pac.data);
            registrarRotuloDeRota(`paciente:${id}`, pac.data?.nomeCompleto);
            setCarteirinhas(Array.isArray(vinculos.data) ? vinculos.data : []);
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        // Só convênios ativos: inativo não aceita carteirinha nova.
        api.get('/planos-saude', { params: { ativo: true } })
            .then(r => setConvenios(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
    }, []);

    const abrirNovo = () => {
        setEditandoId(null);
        setForm({ ...estadoInicial(), padrao: carteirinhas.length === 0 });
        setDialogAberto(true);
    };

    const abrirEdicao = (item) => {
        fecharMenu();
        setEditandoId(item.id_paciente_plano_saude);
        setForm({
            idPlanoSaude: item.idPlanoSaude || '',
            numeroCarteirinha: item.numeroCarteirinha || '',
            validadeCarteirinha: item.validadeCarteirinha || '',
            titular: item.titular ?? true,
            nomeTitular: item.nomeTitular || '',
            ativo: item.ativo ?? true,
            padrao: item.padrao ?? false,
        });
        setDialogAberto(true);
    };

    const set = (campo) => (e) => setForm(prev => ({ ...prev, [campo]: e.target.value }));
    const setBool = (campo) => (e) => setForm(prev => ({ ...prev, [campo]: e.target.checked }));

    const handleSalvar = async () => {
        if (!form.idPlanoSaude) { alertValidacao('Selecione o convênio.'); return; }
        if (!form.numeroCarteirinha.trim()) { alertValidacao('Informe o número da carteirinha.'); return; }
        if (!form.titular && !form.nomeTitular.trim()) {
            alertValidacao('Paciente dependente exige o nome do titular.');
            return;
        }
        if (form.padrao && !form.ativo) {
            alertValidacao('Convênio padrão precisa estar ativo.');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                idPlanoSaude: Number(form.idPlanoSaude),
                numeroCarteirinha: form.numeroCarteirinha.trim(),
                validadeCarteirinha: form.validadeCarteirinha || null,
                titular: form.titular,
                nomeTitular: form.titular ? null : form.nomeTitular.trim(),
                ativo: form.ativo,
                padrao: form.padrao,
            };
            if (editandoId) {
                await api.put(`/pacientes/${id}/convenios/${editandoId}`, payload);
                toastSuccess('Carteirinha atualizada');
            } else {
                await api.post(`/pacientes/${id}/convenios`, payload);
                toastSuccess('Carteirinha cadastrada');
            }
            setDialogAberto(false);
            carregar();
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setSaving(false);
        }
    };

    const handleAlterarStatus = async (item) => {
        fecharMenu();
        const inativando = item.ativo;
        const ok = await confirmAcao({
            title: inativando ? 'Inativar carteirinha' : 'Reativar carteirinha',
            text: inativando
                ? 'A carteirinha deixa de ser oferecida no agendamento, mas o histórico de guias é mantido. Continuar?'
                : 'Reativar esta carteirinha?',
            confirmButtonText: inativando ? 'Inativar' : 'Reativar',
            icon: inativando ? 'warning' : 'question',
            danger: inativando,
        });
        if (!ok) return;
        try {
            await api.patch(`/pacientes/${id}/convenios/${item.id_paciente_plano_saude}/status`, null,
                { params: { ativo: !inativando } });
            toastSuccess(inativando ? 'Carteirinha inativada' : 'Carteirinha reativada');
            carregar();
        } catch {
            /* erro exibido pelo handler global */
        }
    };

    const handleDefinirPadrao = async (item) => {
        fecharMenu();
        try {
            await api.patch(`/pacientes/${id}/convenios/${item.id_paciente_plano_saude}/padrao`);
            toastSuccess('Convênio padrão atualizado');
            carregar();
        } catch {
            /* erro exibido pelo handler global */
        }
    };

    const handleExcluir = async (item) => {
        fecharMenu();
        const ok = await confirmAcao({
            title: 'Excluir carteirinha',
            text: `Remover a carteirinha de ${item.nomePlanoSaude}? Para manter histórico, prefira inativar.`,
            confirmButtonText: 'Excluir',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/pacientes/${id}/convenios/${item.id_paciente_plano_saude}`);
            toastSuccess('Carteirinha excluída');
            carregar();
        } catch {
            /* erro exibido pelo handler global */
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    const temVencida = carteirinhas.some(c => c.ativo && c.vencida);

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate(`/pacientes/${id}/ficha`)} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                        Convênios do paciente
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {paciente?.nomeCompleto}
                    </Typography>
                </Box>
                {!soLeitura && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>
                        Nova carteirinha
                    </Button>
                )}
            </Box>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                {temVencida && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        Há carteirinha ativa com validade vencida. A emissão de guia é recusada quando a carteirinha
                        não cobre a data do atendimento.
                    </Alert>
                )}

                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell><strong>Convênio</strong></TableCell>
                                <TableCell><strong>Carteirinha</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Validade</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Titular</strong></TableCell>
                                <TableCell><strong>Situação</strong></TableCell>
                                <TableCell align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {carteirinhas.length > 0 ? carteirinhas.map((item) => (
                                <TableRow key={item.id_paciente_plano_saude} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BadgeIcon fontSize="small" color="action" />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.nomePlanoSaude}</Typography>
                                            {!item.planoSaudeAtivo && (
                                                <Tooltip title="Convênio inativo">
                                                    <Chip size="small" label="Convênio inativo" color="warning" variant="outlined" />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{item.numeroCarteirinha}</TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                        {item.validadeCarteirinha ? formatarData(item.validadeCarteirinha) : 'Sem validade'}
                                        {item.vencida && <Chip size="small" label="Vencida" color="error" sx={{ ml: 1 }} />}
                                    </TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                        {item.titular ? 'O próprio paciente' : item.nomeTitular || '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            <Chip size="small" label={item.ativo ? 'Ativa' : 'Inativa'}
                                                color={item.ativo ? 'success' : 'default'} />
                                            {item.padrao && <Chip size="small" icon={<StarIcon />} label="Padrão" color="primary" />}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={(e) => abrirMenu(e, item)}>
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" sx={{ color: '#999' }}>
                                            Paciente particular: nenhuma carteirinha cadastrada.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Menu anchorEl={menuAnchor.el} open={Boolean(menuAnchor.el)} onClose={fecharMenu}>
                <MenuItem onClick={() => abrirEdicao(menuAnchor.item)}>
                    <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                    <ListItemText>{soLeitura ? 'Visualizar' : 'Editar'}</ListItemText>
                </MenuItem>
                {!soLeitura && (
                    <MenuItem onClick={() => handleDefinirPadrao(menuAnchor.item)}
                        disabled={menuAnchor.item?.padrao || !menuAnchor.item?.ativo}>
                        <ListItemIcon><StarIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText>Definir como padrão</ListItemText>
                    </MenuItem>
                )}
                {!soLeitura && (
                    <MenuItem onClick={() => handleAlterarStatus(menuAnchor.item)}>
                        <ListItemIcon>
                            {menuAnchor.item?.ativo
                                ? <BlockIcon fontSize="small" color="warning" />
                                : <CheckCircleIcon fontSize="small" color="success" />}
                        </ListItemIcon>
                        <ListItemText>{menuAnchor.item?.ativo ? 'Inativar' : 'Reativar'}</ListItemText>
                    </MenuItem>
                )}
                {!soLeitura && (
                    <MenuItem onClick={() => handleExcluir(menuAnchor.item)}>
                        <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText>Excluir</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editandoId ? 'Editar carteirinha' : 'Nova carteirinha'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField select label="Convênio *" value={form.idPlanoSaude} onChange={set('idPlanoSaude')}
                            disabled={soLeitura}>
                            {convenios.map(c => (
                                <MenuItem key={c.id_plano_saude} value={c.id_plano_saude}>{c.nome}</MenuItem>
                            ))}
                        </TextField>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField sx={{ flex: 2, minWidth: 180 }} label="Número da carteirinha *"
                                value={form.numeroCarteirinha} onChange={set('numeroCarteirinha')} disabled={soLeitura} />
                            <TextField sx={{ flex: 1, minWidth: 160 }} type="date" label="Validade"
                                InputLabelProps={{ shrink: true }} value={form.validadeCarteirinha}
                                onChange={set('validadeCarteirinha')} disabled={soLeitura}
                                helperText="Vazio = sem validade." />
                        </Box>
                        <FormControlLabel
                            control={<Switch checked={form.titular} onChange={setBool('titular')} disabled={soLeitura} />}
                            label="O paciente é o titular"
                        />
                        {!form.titular && (
                            <TextField label="Nome do titular *" value={form.nomeTitular} onChange={set('nomeTitular')}
                                disabled={soLeitura} />
                        )}
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            <FormControlLabel
                                control={<Switch checked={form.ativo} onChange={setBool('ativo')} disabled={soLeitura} />}
                                label="Ativa"
                            />
                            <FormControlLabel
                                control={<Switch checked={form.padrao} onChange={setBool('padrao')} disabled={soLeitura || !form.ativo} />}
                                label="Convênio padrão no agendamento"
                            />
                        </Box>
                        <Alert severity="info">
                            O paciente tem um único convênio padrão: marcar este desmarca o anterior. Também não é
                            possível ter duas carteirinhas ativas no mesmo convênio.
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogAberto(false)}>Fechar</Button>
                    {!soLeitura && (
                        <Button variant="contained" onClick={handleSalvar} disabled={saving}>
                            {saving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
